import React, { ReactNode } from "react";
import { ExpandMore } from "@material-ui/icons";
import { ComponentThemeProvider } from "storybook/context/component-theme";
import { Typography } from "storybook/components/typography";

import {
  StyledDetailsDefault,
  StyledSummaryDefault,
  StyledTitleContainer,
  StyledPreviewContainer,
  StyledToggleText,
} from "./theme/default";

const styledDetailsMap = {
  default: StyledDetailsDefault,
} as const;

const styledSummaryMap = {
  default: StyledSummaryDefault,
} as const;

type ExpandCollapseLabels = {
  expand: string | ReactNode;
  collapse: string | ReactNode;
};

export type AccordionType = {
  theme: "default";
  children?: ReactNode;
  title: string | ReactNode;
  previewContent?: ReactNode;
  ariaLabel?: string;
  initialExpanded?: boolean;
  expandCollapseLabels?: ExpandCollapseLabels;
};

export function Accordion({
  title,
  theme,
  children,
  ariaLabel,
  previewContent,
  initialExpanded = false,
  expandCollapseLabels,
}: AccordionType): JSX.Element {
  const StyledDetails = styledDetailsMap[theme];
  const StyledSummary = styledSummaryMap[theme];

  return (
    <ComponentThemeProvider theme={theme}>
      <StyledDetails
        open={initialExpanded}
        aria-label={ariaLabel}
        data-testid="Accordion"
        $expandable={!!children}
        $previewContent={!!previewContent}
      >
        <StyledSummary data-testid="Accordion__Summary">
          <StyledTitleContainer $previewContent={!!previewContent}>
            {typeof title === "string" ? (
              <Typography theme="default" variant="body1" color="ciqBrandDark">
                <b>{title}</b>
              </Typography>
            ) : (
              title
            )}
            {children &&
              (expandCollapseLabels ? (
                <StyledToggleText>
                  <span className="accordion-expand">
                    {expandCollapseLabels.expand}
                  </span>
                  <span className="accordion-collapse">
                    {expandCollapseLabels.collapse}
                  </span>
                </StyledToggleText>
              ) : (
                <ExpandMore />
              ))}
          </StyledTitleContainer>
          {previewContent && children && (
            <StyledPreviewContainer>{previewContent}</StyledPreviewContainer>
          )}
        </StyledSummary>
        {children}
      </StyledDetails>
    </ComponentThemeProvider>
  );
}
